import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import FormInputs from "../UI/FormInputs";
import Buttons from "../UI/Buttons";
import UserService from "../../services/UserService";
import { isSet } from "../../utils/commonUtils";

const KIOSK_PIN_INPUT_PROPS = {
  maxLength: 4,
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
};

/**
 * Staff must enter kiosk PIN to leave kiosk mode and return to the app.
 */
function KioskExitModal({ isOpen, onClose, onSuccess, Toast }) {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (isOpen) {
      setPin("");
      setValidation({});
    }
  }, [isOpen]);

  const canSubmit = useMemo(() => {
    const digits = String(pin || "").replace(/\D/g, "");
    return digits.length === 4;
  }, [pin]);

  const handleClose = () => {
    if (submitting) return;
    setPin("");
    setValidation({});
    onClose?.();
  };

  const handleSubmit = async () => {
    const v = {};
    const digits = String(pin || "").replace(/\D/g, "").slice(0, 4);
    if (digits.length !== 4) v.pin = "PIN must be 4 digits";
    setValidation(v);
    if (Object.keys(v).length) return;

    setSubmitting(true);
    try {
      await UserService.verifyKioskPin(digits);
      setPin("");
      setValidation({});
      onSuccess?.();
    } catch (e) {
      const msg =
        e?.data?.message ||
        e?.message ||
        (typeof e?.data === "string" ? e.data : null) ||
        "Invalid kiosk PIN.";
      Toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={Boolean(isOpen)}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>
          <h6 className="text-lg mb-0 font-weight-bold">Exit kiosk mode</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Enter your kiosk PIN to exit.
        </p>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="row">
            <div className="col-12">
              <label className="mb-0 text-sm text-dark" htmlFor="snap-kiosk-exit-pin">
                Kiosk PIN
              </label>
              <FormInputs
                type="Password"
                id="snap-kiosk-exit-pin"
                className="form-control form-control-solid"
                placeholder="Enter PIN (4 digits)"
                name="snap_kiosk_exit_pin"
                autocomplete="off"
                value={pin}
                inputProps={KIOSK_PIN_INPUT_PROPS}
                changeHandler={(val) => {
                  const digitsOnly = String(val ?? "").replace(/\D/g, "").slice(0, 4);
                  setPin(digitsOnly);
                  setValidation((prev) => ({ ...prev, pin: "" }));
                }}
              />
              {isSet(validation.pin, "") !== "" ? (
                <span className="text-danger m-1">{validation.pin}</span>
              ) : null}
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <Buttons
          className="btn btn-light btn-sm"
          clickHandler={handleClose}
          label="Cancel"
          acl="any"
        />
        <Buttons
          type="Single"
          className="btn btn-primary"
          label={submitting ? "Verifying…" : "Exit"}
          clickHandler={handleSubmit}
          acl="any"
        />
      </Modal.Footer>
    </Modal>
  );
}

KioskExitModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  Toast: PropTypes.shape({ error: PropTypes.func, success: PropTypes.func }).isRequired,
};

export default KioskExitModal;
